import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { getOrderEntryMobileConfigurableColumns } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/utils/configurable-columns-utils';
import {
  IconButton,
  Icons,
  TIMING_PRESETS,
  View,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { Keyboard } from 'react-native';
import { OrderEntryMobileCardFieldsSheet } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-card-fields-sheet';
import { OrderEntryMobileCatalogResultsHeader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-catalog-results-header';
import { OrderEntryMobileListContainer } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/containers/order-entry-mobile-list-container';
import { OrderEntryMobileLoaderArgs } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-mobile-data';
import { OrderEntryMode } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import {
  SearchInput,
  SearchInputRef,
} from '@/experiences/sales-portal/components/search-input';
import { useCanWriteRequestNote } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-can-write-request-note';
import { useConfigureColumns } from '@experiences/components/table/blocs/hooks/use-configure-columns';
import { useDeferredUnmount } from '@/experiences/sales-portal/blocs/hooks/use-deferred-unmount';
import { useOrderEntryCatalogSearch } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-catalog-search';
import { useOrderEntryDataLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-order-entry-data-loader';
import { useTheme } from '@/contexts/theme-context';

interface OrderEntryMobileSearchContainerProps {
  areSubstitutionsEnabled: boolean;
  bottomInset?: number;
  debouncedSaveCart: (() => void) & { flush: () => void };
  isGrossProfitHidden: boolean;
  isQuantityDisabled: boolean;
  loaderArgs: OrderEntryMobileLoaderArgs;
  mode: OrderEntryMode;
  onCatalogSearchCtaChange: (
    state: {
      isEnabled: boolean;
      isVisible: boolean;
      onPress: () => void;
    } | null,
  ) => void;
  onSearchPillChange: (
    state: {
      isVisible: boolean;
      onPress: () => void;
    } | null,
  ) => void;
  showConfidentialFields: boolean;
}

export const OrderEntryMobileSearchContainer: React.FC<
  OrderEntryMobileSearchContainerProps
> = ({
  areSubstitutionsEnabled,
  bottomInset,
  debouncedSaveCart,
  isGrossProfitHidden,
  isQuantityDisabled,
  loaderArgs,
  mode,
  onCatalogSearchCtaChange,
  onSearchPillChange,
  showConfidentialFields,
}) => {
  const theme = useTheme();
  const {
    chatUUID,
    deliveryDate,
    linkedSupplierCollectionUUID,
    orderGuideUUID,
    restaurantUUID,
    salesRepConfig,
    savedSelectedFulfillment,
    supplierTimezone,
    supplierUUID,
  } = loaderArgs;

  const { error, loading, priceEditDisplayHeaders, refetch, tableData } =
    useOrderEntryDataLoader({
      deliveryDate,
      linkedSupplierCollectionUUID,
      orderGuideUUID,
      restaurantUUID,
      salesRepConfig,
      savedSelectedFulfillment,
      supplierTimezone,
      supplierUUID,
    });

  const canWriteRequestNote = useCanWriteRequestNote(supplierUUID, chatUUID);
  const configurableColumnDefs = useMemo(
    () =>
      getOrderEntryMobileConfigurableColumns({
        areSubstitutionsEnabled,
        canWriteRequestNote,
        isFulfilledHidden: false,
        isGrossProfitHidden,
        priceEditDisplayHeaders,
      }),
    [
      areSubstitutionsEnabled,
      canWriteRequestNote,
      isGrossProfitHidden,
      priceEditDisplayHeaders,
    ],
  );

  const {
    columnVisibility,
    configurableColumns,
    configurableColumnsOrder,
    reorderColumns,
    resetToDefaults,
    toggleColumnVisibility,
  } = useConfigureColumns(
    ConfigurationDomain.Ordering,
    TableName.OrderEntryMobileCards,
    configurableColumnDefs,
  );

  const visibleFieldIds = useMemo(
    () =>
      configurableColumnsOrder.filter((id) => columnVisibility[id] !== false),
    [columnVisibility, configurableColumnsOrder],
  );
  const fieldIds = useDeferredValue(visibleFieldIds);
  const labelsById = useMemo(
    () =>
      Object.fromEntries(
        configurableColumns.map((column) => [column.id, column.label]),
      ),
    [configurableColumns],
  );

  const [isCardFieldsOpen, setIsCardFieldsOpen] = useState(false);
  const handleOpenCardFields = useCallback(() => setIsCardFieldsOpen(true), []);
  const shouldRenderCardFields = useDeferredUnmount(
    isCardFieldsOpen,
    TIMING_PRESETS.takeoverSheet.duration,
  );

  const catalogSearch = useOrderEntryCatalogSearch({
    deliveryDate,
    mode,
    orderGuideUUID,
    priceEditDisplayHeaders,
    restaurantUUID,
    salesRepConfig,
    supplierUUID,
  });

  const {
    handleCatalogSearch,
    handleSearchChange,
    isCatalogSearching,
    isInCatalogMode,
    searchTerm,
  } = catalogSearch;
  const canSearchCatalog =
    searchTerm.trim().length > 0 && !isInCatalogMode && !isCatalogSearching;

  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;

  const handlePressSearchCatalog = useCallback(() => {
    handleCatalogSearch(searchTermRef.current);
    Keyboard.dismiss();
  }, [handleCatalogSearch]);

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const handleSearchFocus = useCallback(() => setIsSearchFocused(true), []);
  const handleSearchBlur = useCallback(() => setIsSearchFocused(false), []);

  const handleSubmitEditing = useCallback(() => {
    if (!isInCatalogMode) {
      handlePressSearchCatalog();
    }
  }, [handlePressSearchCatalog, isInCatalogMode]);

  useEffect(() => {
    onCatalogSearchCtaChange({
      isEnabled: canSearchCatalog,
      isVisible: isSearchFocused,
      onPress: handlePressSearchCatalog,
    });
    return () => onCatalogSearchCtaChange(null);
  }, [
    canSearchCatalog,
    handlePressSearchCatalog,
    isSearchFocused,
    onCatalogSearchCtaChange,
  ]);

  const searchInputRef = useRef<SearchInputRef>(null);
  const handleSearchPillPress = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  const isSearchPillVisible = searchTerm.length === 0;
  useEffect(() => {
    onSearchPillChange({
      isVisible: isSearchPillVisible,
      onPress: handleSearchPillPress,
    });
    return () => onSearchPillChange(null);
  }, [handleSearchPillPress, isSearchPillVisible, onSearchPillChange]);

  return (
    <>
      <XStack
        alignItems="center"
        gap={theme.sk.spacing.spaceSmall}
        paddingBottom={theme.sk.spacing.spaceMedium}
        paddingHorizontal={theme.sk.spacing.spaceMedium}
        paddingTop={theme.sk.spacing.spaceMedium}
      >
        <View flex={1}>
          <SearchInput
            onBlur={handleSearchBlur}
            onFocus={handleSearchFocus}
            onSearchChange={handleSearchChange}
            onSubmitEditing={handleSubmitEditing}
            placeholderText={i18n.t(
              isInCatalogMode
                ? 'sales_portal.order_entry.search_catalog_placeholder'
                : 'sales_portal.order_entry.search_placeholder',
            )}
            ref={searchInputRef}
            searchText={searchTerm}
          />
        </View>
        <IconButton
          aria-label={i18n.t('sales_portal.order_entry.card_fields.open')}
          icon={<Icons.Settings2 />}
          onPress={handleOpenCardFields}
          size="$md"
          variant="secondary"
        />
      </XStack>
      {catalogSearch.showCatalogResults ? (
        <OrderEntryMobileCatalogResultsHeader
          count={catalogSearch.catalogResults.length}
          query={catalogSearch.catalogSearchTerm}
        />
      ) : null}
      <OrderEntryMobileListContainer
        areSubstitutionsEnabled={areSubstitutionsEnabled}
        bottomInset={bottomInset}
        catalogSearch={catalogSearch}
        debouncedSaveCart={debouncedSaveCart}
        fieldIds={fieldIds}
        hasLoadError={!!error}
        isGrossProfitHidden={isGrossProfitHidden}
        isLoading={loading}
        isQuantityDisabled={isQuantityDisabled}
        labelsById={labelsById}
        loaderArgs={loaderArgs}
        mode={mode}
        onRetryLoad={refetch}
        priceEditDisplayHeaders={priceEditDisplayHeaders}
        showConfidentialFields={showConfidentialFields}
        tableData={tableData}
      />
      {shouldRenderCardFields ? (
        <OrderEntryMobileCardFieldsSheet
          columnVisibility={columnVisibility}
          configurableColumns={configurableColumns}
          configurableColumnsOrder={configurableColumnsOrder}
          onOpenChange={setIsCardFieldsOpen}
          onReorder={reorderColumns}
          onReset={resetToDefaults}
          onToggle={toggleColumnVisibility}
          open={isCardFieldsOpen}
        />
      ) : null}
    </>
  );
};
